import { createColumnHelper, useTable } from "@tanstack/react-table";
import React, { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { isOrdererAdminWithoutAuthorisedUnits } from "../../common/helpers/municipalityRoles";
import { ListOption } from "../../common/types";
import Button from "../../components/Button";
import { ORDERER_ADMIN_NO_UNITS_MESSAGE } from "../../components/OrdererAdminNoUnitsBanner";
import Container from "../../components/Container";
import DropdownFilter from "../../components/DropdownFilter";
import EmptyState from "../../components/EmptyState";
import Loader from "../../components/Loader";
import Plate from "../../components/Plate";
import SearchBar from "../../components/SearchBar";
import SubHeader from "../../components/SubHeader";
import Table from "../../components/Table/Table";
import { sortingListingFeatures } from "../../components/Table/tableFeatures";
import { AuthenticatedUserContext } from "../../contexts/AuthenticatedUserContext";
import useFilters from "../../hooks/useFilters";
import useSelectedMunicipalityId from "../../hooks/useSelectedMunicipalityId";
import { useAllUnitsQuery } from "../../queries/units/useAllUnitsQuery";
import { useAllOrderersQuery } from "../../queries/useAllOrderersQuery";
import { mapTruthyValuesToQueryStringParams } from "../../utils/urlUtils";
import {
    filterOrderersAuthorizedUnits,
    filterOrderersBySearchQuery,
    formatOrdererListingUnits,
} from "./OrdererView.utils";
import { OrdererListingItem } from "./ordererTypes";

const columnHelper = createColumnHelper<
    typeof sortingListingFeatures,
    OrdererListingItem
>();
const columns = columnHelper.columns([
    columnHelper.accessor(
        (row) => row.orderer.firstName + " " + row.orderer.lastName,
        {
            id: "name",
            header: () => "Namn",
            cell: (props) => props.getValue(),
            meta: {
                getCellContext: () => {
                    return {
                        length: 3,
                    };
                },
            },
        }
    ),
    columnHelper.accessor((row) => row.orderer.municipalEmail, {
        id: "email",
        header: () => "E-postadress",
        enableSorting: false,
        cell: (props) => props.getValue(),
        meta: {
            getCellContext: () => {
                return {
                    length: 3,
                };
            },
        },
    }),
    columnHelper.accessor(
        (row) => formatOrdererListingUnits(row.authUnits),
        {
            id: "units",
            header: () => "Enhet",
            enableSorting: false,
            cell: (props) => props.getValue(),
            meta: {
                getCellContext: () => {
                    return {
                        length: 3,
                    };
                },
            },
        }
    ),
]);

const OrdererListingView = () => {
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const municipalityId = useSelectedMunicipalityId();
    const searchQuery = new URLSearchParams(search).get("query") || "";
    const updateSearchQueryString = (value: string) => {
        const params = mapTruthyValuesToQueryStringParams(search, {
            query: value,
        });

        navigate({
            pathname,
            search: params.toString(),
        });
    };
    const { authUserData } = useContext(AuthenticatedUserContext);

    const allUnitsQuery = useAllUnitsQuery();

    const [unitFilters, setUnitFilters] = useFilters(
        "orderers",
        municipalityId,
        authUserData?.identityId
    );

    const [filterExpanded, setFilterExpanded] = useState(false);

    const allOrderersQuery = useAllOrderersQuery(
        unitFilters?.map(({ id }) => id)
    );

    const authorisedUnitCodes = municipalityId
        ? authUserData?.municipalityAuthorisedUnitCodesMap[municipalityId]
        : undefined;
    const hasNoAuthorisedUnits = isOrdererAdminWithoutAuthorisedUnits(
        authUserData?.municipalityRole,
        authorisedUnitCodes,
    );

    const data = useMemo(() => {
        if (!allOrderersQuery.data) {
            return undefined;
        }

        const mapped = filterOrderersAuthorizedUnits(
            allOrderersQuery.data,
            allUnitsQuery.data ?? [],
        );

        return filterOrderersBySearchQuery(mapped, searchQuery);
    }, [allOrderersQuery.data, allUnitsQuery.data, searchQuery]);

    const table = useTable({
        features: sortingListingFeatures,
        data: data || [],
        columns: columns,
    });

    const setFilterChoice = (selectedFilter: ListOption) => {
        const alreadySelected = unitFilters.find(
            (filter) => filter.id === selectedFilter.id
        );

        if (alreadySelected) {
            const updatedFilters = [...unitFilters].filter(
                (filter) => filter.id !== selectedFilter.id
            );
            setUnitFilters(updatedFilters);
        } else {
            setUnitFilters([...unitFilters, selectedFilter]);
        }
    };

    return (
        <Container>
            <SearchBar
                placeholder="Sök beställare (förnamn, efternamn, e-post, telefon eller enhet)"
                key={searchQuery}
                initialValue={searchQuery}
                onEnterPress={(value) => {
                    updateSearchQueryString(value);
                }}
            />
            <SubHeader
                title="Beställare"
                leftContent={
                    allUnitsQuery.isSuccess &&
                    !!allUnitsQuery.data.length && (
                        <DropdownFilter
                            data={allUnitsQuery.data.map((unit) => ({
                                id: unit.code,
                                name: unit.name,
                            }))}
                            selectedData={unitFilters}
                            clearFilter={() => setUnitFilters([])}
                            expandedCallback={(expanded) =>
                                setFilterExpanded(expanded)
                            }
                            placeholder="Alla enheter"
                            itemName="Enheter"
                            setChoice={setFilterChoice}
                        />
                    )
                }
                rightContent={
                    !hasNoAuthorisedUnits ? (
                        <Button
                            size="small"
                            onClick={() => {
                                navigate(pathname + "/skapa-ny");
                            }}
                            title="Skapa ny"
                        />
                    ) : undefined
                }
            />
            <Plate
                spaceBottom={true}
                overScroll
                faded={filterExpanded || allOrderersQuery.isFetching}>
                {hasNoAuthorisedUnits ? (
                    <EmptyState text={ORDERER_ADMIN_NO_UNITS_MESSAGE} />
                ) : allUnitsQuery.isLoading || allOrderersQuery.isLoading ? (
                    <Loader />
                ) : allOrderersQuery.isError ? (
                    <EmptyState text="Kunde inte hämta beställare." />
                ) : !allOrderersQuery.data?.length ? (
                    <EmptyState text="Det finns inga beställare." />
                ) : !data?.length ? (
                    <EmptyState text="Inga beställare matchar sökningen." />
                ) : (
                    <Table
                        table={table}
                        getLinkTo={(data: OrdererListingItem) =>
                            pathname + "/" + data.orderer.uid
                        }
                    />
                )}
            </Plate>
        </Container>
    );
};

export default OrdererListingView;
